/**
 * useIsDesktop — a JS mirror of the Tailwind `md` (≥768px) breakpoint.
 *
 * Most of the chat layout is CSS-driven (Tailwind `md:` utilities), but some
 * surfaces branch STRUCTURALLY on a boolean rather than on CSS, e.g. the
 * citation `BottomSheet` (`desktop` → slides in from the right vs. up from the
 * bottom). This hook supplies that
 * boolean from a single `matchMedia` listener, SSR-safe (returns `false` when
 * `window` is absent) and re-rendering only on the actual breakpoint crossing.
 *
 * Mirrors `features/shops/useIsDesktop.ts` (same contract); kept local to the
 * chat feature to respect the per-feature boundary.
 */
import { useEffect, useState } from "react";

/** The `md` breakpoint (Tailwind default). */
export const DESKTOP_QUERY = "(min-width: 768px)";

function readMatch(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia(DESKTOP_QUERY).matches;
}

export function useIsDesktop(): boolean {
  const [isDesktop, setIsDesktop] = useState<boolean>(readMatch);

  useEffect(() => {
    if (
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function"
    ) {
      return undefined;
    }
    const mql = window.matchMedia(DESKTOP_QUERY);
    const onChange = (event: MediaQueryListEvent): void => {
      setIsDesktop(event.matches);
    };
    // Sync once in case the value changed between initial read and effect.
    setIsDesktop(mql.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  return isDesktop;
}
